//* react
import React from 'react';

//* material-ui
import Grid from '@mui/material/Grid2';
import { Box, Link } from '@mui/material';

//* types
import { ChatUserModel } from 'types/chat-users/chat-user';
import {
  ConversationOpenStatusSocketType,
  LabelUpdateSocketType,
  ReceiveMessageSocketType,
  ReceiveTemplateMessageSocketType,
  SocketEventType,
  SocketHistoryMessageModel,
  UpdateChatUserDontDisturbSocketType,
  UpdateChatUserUnreadSocketType,
  UpdateCompanyPlatformsSocketType,
  UpdateMessageSocketType,
} from 'types/socket';

//* system
import { dispatch, store } from 'store';
import { doesUserMatchCurrentFilters, getMessageStatus } from 'utils/chat';
import {
  saveSingleMessageToUser,
  setChatUsers,
  setSelectedChatUser,
  updateSingleChatUser,
  updateSingleChatUserByPhone,
} from 'store/slices/chat';
import { getBrowserNotifStatus, handleBrowserNotification, safeParseJSON } from 'utils/helpers';
// import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import useAuth from './useAuth';
import { updateSingleApplicationUser } from 'store/slices/applicationUsers';
import { CompanyPlatformsModel } from 'types/company-platforms/companyPlatforms';
import { updateCompanyPlatformsData } from 'store/slices/companyPlatforms';
import { updateCompanyData } from 'store/slices/company';
import { useSocketListeners } from './useSocketListeners';
import { useSnackbar } from 'notistack';
import NotificationIcon from 'ui-component/third-party/NotificationIcon';
import { ChatViewUsersId, getCurrentView } from './useCurrentChatView';
import { getUnreadChatUsers } from 'store/slices/unread-chat-users';
import { getChatUser } from 'api/chat-users';

/**
 * Hook for handling global socket events
 */

const socketEvents: Array<SocketEventType> = [
  'ReceiveMessage',
  'MessageStatusUpdate',
  'ReceiveTemplateMessage',
  'unreadNotification',
  'UnreadUpdate',
  'updateChatUserUnread',
  'Notification',
  'TaggedUsersNotification',
  'labelUpdate',
  'conversationOpenStatus',
  'funnerServiceNotification',
  'UpdateChatUserDontDisturb',
  'updateFunnerUserActive',
  'UpdateCompanyPlatforms',
  'ContactBotStatusChange',
  'Logout',
];

export default function useGlobalSocketEvents() {
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  //* "Receive message"
  const insertUsersAccordingToFilters = React.useCallback(function (
    currentUser: ChatUserModel,
    newUsersList: Array<ChatUserModel>,
    view: ChatViewUsersId,
  ) {
    const doesUserMatchFilters = doesUserMatchCurrentFilters(currentUser, view);


    if (doesUserMatchFilters) {
      if (newUsersList.length === 0) {
        updateSingleChatUser(currentUser.Id, { IsOpen: true });
      }
      newUsersList.unshift(currentUser);
    }

    return newUsersList;
  }, []);
  const receiveMessageUpdateChatUserInView = React.useCallback(
    (view: ChatViewUsersId, newMessage: SocketHistoryMessageModel) => {
      const storeState = store.getState();
      const areUsersRequested = storeState.chat.areUsersRequested;
      
      if (!areUsersRequested[`${view}`]) {
        console.log(`[DEBUG Socket] ${view} - returning [] because areUsersRequested is false`);
        return [];
      }

      const chatUsers = storeState.chat.chatUsers[`${view}`];

      const newChatUser = { ...newMessage.resultChatUser };
      const targetUser = chatUsers.find(
        (user) => user.UserDisplayPhoneNumber === newMessage.resultChatUser?.UserDisplayPhoneNumber,
      );


      const isSameMessage =
        targetUser?.LastMessageType === newChatUser?.LastMessageType &&
        (newChatUser?.LastMessageType === 'outcoming'
          ? targetUser?.OutcomingLastMessageId === newChatUser?.OutcomingLastMessageId
          : targetUser?.incomingLastMessageId === newChatUser?.incomingLastMessageId);

      let newUsersList: Array<ChatUserModel> = [];
      if (isSameMessage) {
        newUsersList = chatUsers.map((user) => {
          if (user.UserDisplayPhoneNumber === newChatUser.UserDisplayPhoneNumber) {
            return newChatUser;
          }

          return user;
        });
      } else {
        newUsersList = chatUsers.filter(
          (user) => user.UserDisplayPhoneNumber !== newChatUser.UserDisplayPhoneNumber,
        );
        

        //* Relate inserting user with active filters
        newUsersList = insertUsersAccordingToFilters(newChatUser, newUsersList, view);
      }

      console.log(`[DEBUG Socket] ${view} - chatUsers count AFTER:`, newUsersList.length);
      return newUsersList;
    },
    [insertUsersAccordingToFilters],
  );
  const onSocketReceiveMessage = React.useCallback(
    function (messageObject: ReceiveMessageSocketType) {
      try {
        //TODO if (applicationType === 'extension') checkAndUpdateFireberryPerformanceLog();
        const newMessage = messageObject.messages[0];

        const storeState = store.getState();
        const companyId = storeState.user?.CompanyId;
        const { chatUsers, selectedChatUser } = storeState.chat;


        if (
          !newMessage.Id ||
          newMessage.resultChatUser.CompanyID !== companyId ||
          newMessage.FileType === 'info'
        )
          return;

        const newChatUser = { ...newMessage.resultChatUser };

        //* Fill & update views with new chat user
        const newChatUsers: typeof chatUsers = (
          Object.keys(chatUsers) as Array<keyof typeof chatUsers>
        ).reduce(
          (acc, key) => {
            if (key === 'loading') {
              acc[key] = chatUsers[key];
            } else {
              acc[key] = receiveMessageUpdateChatUserInView(key, newMessage);
            }

            return acc;
          },
          {} as typeof chatUsers,
        );


        dispatch(
          setChatUsers({
            allChatUsers: newChatUsers.allChatUsers,
            myChatUsers: newChatUsers.myChatUsers,
            notMyChatUsers: newChatUsers.notMyChatUsers,
            unassignedChatUsers: newChatUsers.unassignedChatUsers,
            botsChatUsers: newChatUsers.botsChatUsers,
            customChatUsers: newChatUsers.customChatUsers,
          }),
        );

        //* Update selected chat user if it is his number
        if (selectedChatUser?.UserDisplayPhoneNumber === newChatUser.UserDisplayPhoneNumber) {
          dispatch(setSelectedChatUser(newChatUser));
        }

        //* update saved messages for this user if exists
        const thisUserSavedMessages = storeState.chat.chatUsersSavedMessages.find(
          (usr) => usr.id === messageObject.phone,
        );
        if (thisUserSavedMessages) {
          dispatch(saveSingleMessageToUser({ id: messageObject.phone, message: newMessage }));
        }

        //* update unread chat users - now handled by server, refresh counts
        getUnreadChatUsers();
      } catch (error: any) {
        console.log('onSocketReceiveMessage error', error);
      }
    },
    [receiveMessageUpdateChatUserInView],
  );
  //* Update message
  const onSocketUpdateMessage = React.useCallback((messageObject: UpdateMessageSocketType) => {
    try {
      const value = messageObject.entry[0].changes[0].value;
      const statuses = value.statuses?.[0];
      if (!statuses) return;
      const storeState = store.getState();
      const { chatUsersSavedMessages } = storeState.chat;

      const thisUserSavedMessage = chatUsersSavedMessages.find((usr) => usr.id === statuses.recipient_id);
      if (!thisUserSavedMessage) return;

      let existingMessage = thisUserSavedMessage.messages.find(
        (msg) => msg.messageId === statuses.id || msg.MessageId === statuses.id,
      );
      if (!existingMessage) return;

      existingMessage = {
        ...existingMessage,
        Status: getMessageStatus(statuses.status ?? '', existingMessage.Status ?? ''),
      };
      dispatch(saveSingleMessageToUser({ id: statuses.recipient_id, message: existingMessage }));
    } catch (error) {
      console.warn(error);
    }
  }, []);
  //* ReceiveTemplateMessage
  const receiveTemplateMessageUpdateChatUserInView = React.useCallback(
    (view: ChatViewUsersId, messageObject: ReceiveTemplateMessageSocketType) => {
      const storeState = store.getState();
      const areUsersRequested = storeState.chat.areUsersRequested;
      
      if (!areUsersRequested[`${view}`]) {
        console.log(`[DEBUG Socket Template] ${view} - returning [] because areUsersRequested is false`);
        return [];
      }

      const chatUsers = storeState.chat.chatUsers[`${view}`];

      let targetUser = chatUsers.find((user) => user.UserDisplayPhoneNumber === messageObject.phone);
      if (!targetUser) {
        console.log(`[DEBUG Socket Template] ${view} - targetUser not found, returning chatUsers as-is`, chatUsers.length);
        return chatUsers;
      }


      targetUser = {
        ...targetUser,
        LastMessage: messageObject.displayMessage,
        LastMessageDate: messageObject.date,
        //? LastMessageMediaType: 'template',
        LastMessageType: 'outcoming',
        OutcomingLastMessage: messageObject.displayMessage,
        OutcomingLastMessageStatus: messageObject.Status,
      };

      // FIX: Use chatUsers (current view) instead of allChatUsers
      let newUsersList = chatUsers.filter(
        (user) => user.UserDisplayPhoneNumber !== messageObject.phone,
      );
      
      
      newUsersList = insertUsersAccordingToFilters(targetUser, newUsersList, view);

      return newUsersList;
    },
    [insertUsersAccordingToFilters],
  );
  const onSocketReceiveTemplateMessage = React.useCallback(
    (messageObject: ReceiveTemplateMessageSocketType) => {
      try {
        const storeState = store.getState();
        const { chatUsers } = storeState.chat;

        

        //* Fill & update views with new chat user
        const newChatUsers: typeof chatUsers = (
          Object.keys(chatUsers) as Array<keyof typeof chatUsers>
        ).reduce(
          (acc, key) => {
            if (key === 'loading') {
              acc[key] = chatUsers[key];
            } else {
              acc[key] = receiveTemplateMessageUpdateChatUserInView(key, messageObject);
            }

            return acc;
          },
          {} as typeof chatUsers,
        );

        
        dispatch(
          setChatUsers({
            allChatUsers: newChatUsers.allChatUsers,
            myChatUsers: newChatUsers.myChatUsers,
            notMyChatUsers: newChatUsers.notMyChatUsers,
            unassignedChatUsers: newChatUsers.unassignedChatUsers,
            botsChatUsers: newChatUsers.botsChatUsers,
            customChatUsers: newChatUsers.customChatUsers,
          }),
        );

        //* Update selected chat user if it is his number
        if (storeState.chat.selectedChatUser?.UserDisplayPhoneNumber === messageObject.phone) {
          dispatch(
            setSelectedChatUser({
              ...storeState.chat.selectedChatUser,
              LastMessage: messageObject.displayMessage,
              LastMessageDate: messageObject.date,
              //? LastMessageMediaType: 'template',
              LastMessageType: 'outcoming',
              OutcomingLastMessage: messageObject.displayMessage,
              OutcomingLastMessageStatus: messageObject.Status,
            }),
          );
        }

        //* update saved messages for this user if exists
        const thisUserSavedMessages = storeState.chat.chatUsersSavedMessages.find(
          (usr) => usr.id === messageObject.phone,
        );
        if (!thisUserSavedMessages) return;

        let currentMessage = thisUserSavedMessages.messages.find(
          (msg) =>
            (msg.TempId && msg.TempId === messageObject.tempId) ||
            (msg.tempId && msg.tempId === messageObject.tempId),
        );
        if (!currentMessage) return;
        currentMessage = {
          ...currentMessage,
          MessageId: messageObject.messageId,
          Status: messageObject.Status,
          StatusHeb: messageObject.StatusHeb,
        };

        dispatch(saveSingleMessageToUser({ id: messageObject.phone, message: currentMessage }));
      } catch (error) {
        console.warn('onSocketReceiveTemplateMessage error: ', error);
      }
    },

    [receiveTemplateMessageUpdateChatUserInView],
  );
  //? Unread Notification

  //* Unread Update
  const onSocketUpdateChatUserUnread = React.useCallback(async function (
    messageObject: UpdateChatUserUnreadSocketType,
  ) {
    try {
      if (messageObject.source === 'setReadAPI') {
        updateSingleChatUser(messageObject.id, { UnreadMessagesNumber: messageObject.unreadNumber });

        //* update unread users
        const storeState = store.getState();
        //* update unread chat users - now handled by server, refresh counts
        getUnreadChatUsers();

        //* If isOnlyUnread filter is active and unreadNumber is 0, remove user from filtered lists
        if (storeState.chat.chatUsersFilters.isOnlyUnread && messageObject.unreadNumber === 0) {
          const updatedChatUsers: typeof storeState.chat.chatUsers = (
            Object.keys(storeState.chat.chatUsers) as Array<keyof typeof storeState.chat.chatUsers>
          ).reduce(
            (acc, key) => {
              if (key === 'loading') {
                acc[key] = storeState.chat.chatUsers[key];
              } else {
                acc[key] = storeState.chat.chatUsers[key].filter(
                  (user) => user.Id !== messageObject.id,
                );
              }
              return acc;
            },
            {} as typeof storeState.chat.chatUsers,
          );

          dispatch(
            setChatUsers({
              allChatUsers: updatedChatUsers.allChatUsers,
              myChatUsers: updatedChatUsers.myChatUsers,
              notMyChatUsers: updatedChatUsers.notMyChatUsers,
              unassignedChatUsers: updatedChatUsers.unassignedChatUsers,
              botsChatUsers: updatedChatUsers.botsChatUsers,
              customChatUsers: updatedChatUsers.customChatUsers,
            }),
          );
        }
      }
    } catch (error) {
      console.warn('onSocketUpdateChatUserUnread error: ', error);
    }
  }, []);
  //TODO Extension browser notifications
  //* Notifications
  const onSocketNotification = React.useCallback(
    (messageObject: any) => {
      try {
        const storeState = store.getState();
        const enableCustomTime = storeState.userConfig.enableCustomTime;
        const linkToObjectNum = storeState.company?.LinkToObjectNum;

        if (enableCustomTime) {
          const phone = messageObject.displayPhoneNumber;
          const currentUser = storeState.chat.selectedChatUser;
          if (phone === currentUser?.UserDisplayPhoneNumber) return;

          const targetLink =
            storeState.company?.CompanyType === 'fireberry' &&
            (messageObject.fireberryAccountLink
              ? messageObject.fireberryAccountLink
              : `https://app.powerlink.co.il/app/views/${
                  linkToObjectNum && linkToObjectNum === 'second' ? '2' : '1'
                }?q=${phone}`);

          if (getBrowserNotifStatus() === 'granted') {
            //? handleBrowserNotification(`Funner - ${targetUser.FullName}`, messageObject.notificationText);
            handleBrowserNotification(`Funner`, messageObject.notificationText);
          } else {
            const currentView = getCurrentView();
            enqueueSnackbar(
              <Grid container spacing={0.5}>
                <Grid size={12}>
                  <Box
                    sx={{ color: 'info.contrastText' }}
                    component={Link}
                    href={currentView.path + '/' + phone}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(currentView.path + '/' + phone);
                    }}
                  >
                    <Grid container spacing={1}>
                      {/* //TODO <Grid>{targetUser?.FullName || ''}</Grid> */}
                      <Grid>{phone}</Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid size={12}>{messageObject.notificationText}</Grid>
              </Grid>,
              {
                variant: 'info',
                // @ts-ignore
                iconVariant: storeState.company?.CompanyType === 'fireberry' && {
                  info: <NotificationIcon type={'info'} data={{ link: targetLink }} />,
                },
              },
            );
          }
        }
      } catch (error) {
        console.warn('onSocketNotification error: ', error);
      }
    },
    [enqueueSnackbar, navigate],
  );
  const onSocketTaggedUsersNotification = React.useCallback(
    (messageObject: any) => {
      try {
        const storeState = store.getState();
        const user = storeState.user;
        if (user?.Udid !== messageObject.notifyContactUserglobalid) return;

        const enableCustomTime = storeState.userConfig.enableCustomTime;
        if (enableCustomTime) {
          const phone = messageObject.displayPhoneNumber;
          // const targetUser = chatUsersGlobalRef.current.notMyChats.find(
          //   (user) => user.UserDisplayPhoneNumber === phone,
          // );

          if (getBrowserNotifStatus() === 'granted') {
            //TODO handleBrowserNotification(`Funner - ${targetUser.FullName}`, messageObject.notificationText);
            handleBrowserNotification(`Funner`, messageObject.notificationText);
          } else {
            const currentView = getCurrentView();
            enqueueSnackbar(
              <Grid container spacing={0.5}>
                <Grid size={12}>
                  <Box
                    sx={{ color: 'info.contrastText' }}
                    component={Link}
                    href={currentView.path + '/' + phone}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(currentView.path + '/' + phone);
                    }}
                  >
                    <Grid container spacing={1}>
                      {/* //TODO <Grid>{targetUser?.FullName || ''}</Grid> */}
                      <Grid>{messageObject.displayPhoneNumber}</Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid size={12}>{messageObject.notificationText}</Grid>
              </Grid>,
              {
                variant: 'info',
              },
            );
          }
        }
      } catch (error) {
        console.warn('onSocketTaggedUsersNotification error: ', error);
      }
    },
    [enqueueSnackbar, navigate],
  );
  const onSocketFunnerServiceNotification = React.useCallback(
    (messageObject: any) => {
      try {
        const { notificationText, type, durationSeconds } = messageObject;
        const scheme = type.split('/');
        enqueueSnackbar(notificationText, {
          autoHideDuration: durationSeconds * 1000,
          variant: 'info',
          style: {
            backgroundColor: scheme[0],
            color: scheme[1],
          },
          // @ts-ignore
          iconVariant: {
            info: (
              <NotificationIcon
                type="info"
                customColors={{
                  bgcolor: scheme[0],
                  color: scheme[1],
                }}
              />
            ),
          },
        });
      } catch (error) {
        console.warn('onSocketFunnerServiceNotification error: ', error);
      }
    },
    [enqueueSnackbar],
  );
  //* Chat user updates
  const onSocketLabelUpdate = React.useCallback(function (messageObject: LabelUpdateSocketType) {
    try {
      updateSingleChatUserByPhone(messageObject.phoneId, { LableName: messageObject.label });
      // Label update doesn't affect unread counts, no need to refresh
    } catch (error) {
      console.log('onSocketLabelUpdate error: ', error);
    }
  }, []);
  const onSocketConversationOpenStatus = React.useCallback(
    (messageObject: ConversationOpenStatusSocketType) => {
      try {
        const { chatUserPhoneId, isOpen } = messageObject;
        updateSingleChatUserByPhone(chatUserPhoneId, { IsOpen: isOpen });
        // Conversation open/close status doesn't affect unread counts, no need to refresh

        //? const storeState = store.getState();
        // const { chatUsers } = storeState.chat;

        // const { open } = storeState.chat.chatUsersFilters;
        // const doesUserMatchFilters =
        //   open === 'open' ? isOpen === true : open === 'close' ? isOpen === false : true;

        // const newChatUsers: typeof chatUsers = (
        //   Object.keys(chatUsers) as Array<keyof typeof chatUsers>
        // ).reduce(
        //   (acc, key) => {
        //     if (key === 'loading') {
        //       acc[key] = chatUsers[key];
        //     } else {
        //       if (!doesUserMatchFilters) {
        //         acc[key] = chatUsers[key].filter((user) => user.UserDisplayPhoneNumber !== chatUserPhoneId);
        //       }
        //     }

        //     return acc;
        //   },
        //   {} as typeof chatUsers,
        // );
      } catch (error) {
        console.warn('onSocketConversationOpenStatus error: ', error);
      }
    },
    [],
  );
  const onSocketUpdateChatUserDontDisturb = React.useCallback(
    (messageObject: UpdateChatUserDontDisturbSocketType) => {
      try {
        const dontDisturb = messageObject.chatUser.DontDisturb;
        updateSingleChatUser(messageObject.chatUser.Id, { DontDisturb: dontDisturb });
        // DontDisturb status doesn't affect unread counts, no need to refresh
      } catch (error) {
        console.warn('onSocketUpdateChatUserDontDisturb error: ', error);
      }
    },
    [],
  );
  const onSocketContactBotStatusChange = React.useCallback((messageObject: any) => {
    try {
      const { UserId, isBot } = messageObject;
      updateSingleChatUserByPhone(UserId, { isBot });
      // Bot status change doesn't affect unread counts, no need to refresh
    } catch (error) {
      console.log('onSocketUpdateFunnerUserActive error: ', error);
    }
  }, []);

  //* Update app users
  const onSocketUpdateFunnerUserActive = React.useCallback(
    (messageObject: any) => {
      try {
        const { funnerUserId, isActive } = messageObject;
        const user = store.getState().user;
        if (funnerUserId === user?.Udid) return logout();

        dispatch(
          updateSingleApplicationUser({
            userId: funnerUserId,
            params: {
              IsActive: isActive,
            },
          }),
        );
      } catch (error) {
        console.warn('onSocketUpdateFunnerUserActive error: ', error);
      }
    },
    [logout],
  );
  //* Company platforms update
  const onSocketCompanyPlatformsChanged = React.useCallback(
    (messageObject: UpdateCompanyPlatformsSocketType) => {
      try {
        const { ChangeNiceTextObject, EditorName, NewObjectJson } = messageObject;

        const parsedPlatforms: CompanyPlatformsModel = JSON.parse(NewObjectJson);
        dispatch(updateCompanyPlatformsData(parsedPlatforms));
        // Keep company.CompanyPlatforms in sync so fetchUpdateCompanyPlatforms
        // uses current data even when no explicit save has been made locally.
        dispatch(updateCompanyData({ CompanyPlatforms: NewObjectJson }));

        // Don't show notification if current user made the change
        const currentUser = store.getState().user;
        const isCurrentUser = currentUser?.FullName === EditorName || currentUser?.FirstName === EditorName;
        
        if (!isCurrentUser) {
          enqueueSnackbar(ChangeNiceTextObject + ' by ' + EditorName, {
            variant: 'info',
          });
        }
      } catch (error) {
        console.warn('onSocketCompanyPlatformsChanged error: ', error);
      }
    },
    [],
  );

  const onSocketNotif = React.useCallback(
    (type: SocketEventType, message: string | null) => {
      const messageObject = safeParseJSON(message);
      console.log('Socket message received: ', type, messageObject);
      if (!messageObject) return;
      switch (type) {
        case 'ReceiveMessage':
          onSocketReceiveMessage(messageObject);
          break;
        case 'MessageStatusUpdate':
          onSocketUpdateMessage(messageObject);
          break;
        case 'ReceiveTemplateMessage':
          onSocketReceiveTemplateMessage(messageObject);
          break;
        case 'unreadNotification':
          break;
        case 'UnreadUpdate':
          onSocketUpdateChatUserUnread(messageObject);
          break;
        case 'updateChatUserUnread':
          onSocketUpdateChatUserUnread(messageObject);
          break;
        case 'Notification':
          onSocketNotification(messageObject);
          break;
        case 'TaggedUsersNotification':
          onSocketTaggedUsersNotification(messageObject);
          break;
        case 'funnerServiceNotification':
          onSocketFunnerServiceNotification(messageObject);
          break;
        case 'labelUpdate':
          onSocketLabelUpdate(messageObject);
          break;
        case 'conversationOpenStatus':
          onSocketConversationOpenStatus(messageObject);
          break;
        case 'UpdateChatUserDontDisturb':
          onSocketUpdateChatUserDontDisturb(messageObject);
          break;
        case 'ContactBotStatusChange':
          onSocketContactBotStatusChange(messageObject);
          break;
        case 'updateFunnerUserActive':
          onSocketUpdateFunnerUserActive(messageObject);
          break;
        case 'UpdateCompanyPlatforms':
          onSocketCompanyPlatformsChanged(messageObject);
          break;
        case 'Logout':
          logout();
          break;
        default:
          break;
      }

      //TODO if (applicationType === 'extension') {
      //   const sendContent = {
      //     messageObject: messageObject,
      //   };

      //   sendMessageToBackground('socketReceiveMessage', sendContent);
      // }
    },
    [
      logout,
      onSocketCompanyPlatformsChanged,
      onSocketContactBotStatusChange,
      onSocketConversationOpenStatus,
      onSocketFunnerServiceNotification,
      onSocketLabelUpdate,
      onSocketNotification,
      onSocketReceiveMessage,
      onSocketReceiveTemplateMessage,
      onSocketTaggedUsersNotification,
      onSocketUpdateChatUserDontDisturb,
      onSocketUpdateChatUserUnread,
      onSocketUpdateFunnerUserActive,
      onSocketUpdateMessage,
    ],
  );

  useSocketListeners(socketEvents, onSocketNotif);
}
